import React, { useLayoutEffect, useRef, useState } from 'react';
import { cn } from 'cn';
import Button from './Button';
import { Popover, PopoverContent, PopoverTrigger } from '../pilot-ui/popover';

export interface ActionRowProps extends React.ComponentProps<'div'> {
  /** Why a button in the row is disabled, or nothing (false, null or ''). */
  reason?: string | false | null;
  /** The reason's id, which the disabled button's aria-describedby names.
   *  Required whenever `reason` can be shown. */
  reasonId?: string;
  /** `status` announces the reason when it appears. */
  reasonRole?: 'status';
  /** `end` (dialog footers): the buttons sit at the end and the reason fills
   *  the start of the row. `start` (inline forms): the buttons sit at the start
   *  and the reason fills the space after them. */
  align?: 'start' | 'end';
}

/** The reason, cut to two lines on screen. When it is longer, a More button
 *  beside it opens the full text in a popover, so it can be read by touch and
 *  keyboard as well as by screen readers, which read the whole element. */
const Reason = ({ text, id, role }: { text: string; id?: string; role?: 'status' }) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [clipped, setClipped] = useState(false);
  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node) return undefined;
    const measure = () => setClipped(node.scrollHeight > node.clientHeight);
    measure();
    // The reason's own width decides whether it is cut, and that width also
    // changes without a window resize, e.g. when a panel opens beside the row.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [text]);
  return (
    <>
      <p ref={textRef} id={id} role={role} className="line-clamp-2 min-w-0 text-xs text-muted-foreground">
        {text}
      </p>
      {clipped && (
        <Popover>
          <PopoverTrigger render={<Button variant="link" size="sm" className="shrink-0" title="Show the full reason" />}>
            More
          </PopoverTrigger>
          <PopoverContent aria-label="Full reason" className="text-xs">{text}</PopoverContent>
        </Popover>
      )}
    </>
  );
};

/**
 * A row of action buttons with a reserved area for the reason one of them is
 * disabled (FS-1077). The buttons share one group and the reason has its own
 * area, so it never sits between two buttons. The area is always laid out,
 * shown or empty, at a fixed height of two lines of the reason's text: beside
 * the buttons when the row itself is wide, where it takes only the width they
 * leave, and under them when the row is narrow (on a phone, or in a narrow
 * table column on any screen). The row's own width decides, through a
 * container query, not the window's. Showing, hiding or wrapping the reason
 * therefore never changes the row's size, so no button moves, even in a
 * dialog centered on its own height. A reason longer than two lines is cut to
 * two on screen with a More button that opens the full text; the element the
 * button's aria-describedby names always holds the full text.
 *
 * As a size container the row never takes its width from its buttons: where
 * it sits decides it. A block or a dialog footer item with flex-1 gives it
 * that; beside other content in a flex row, give it a basis.
 */
const ActionRow = ({ reason, reasonId, reasonRole, align = 'start', className, children, ...props }: ActionRowProps) => (
  <div className={cn('@container', className)} {...props}>
    {/* @sm is a 24rem row: narrower than that, the reason goes under the buttons. */}
    <div className="flex flex-col gap-2 @sm:flex-row @sm:items-center">
      <div className={cn('flex flex-wrap items-center gap-2', align === 'end' && 'justify-end')}>
        {children}
      </div>
      {/* h-8 is two lines of text-xs (line height 1rem). */}
      <div className={cn('flex h-8 min-w-0 items-center gap-1 @sm:flex-1', align === 'end' && '@sm:order-first')}>
        {reason && <Reason text={reason} id={reasonId} role={reasonRole} />}
      </div>
    </div>
  </div>
);

export default ActionRow;
